import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UiThemeService } from 'src/app/core/services/ui-theme.service';

export type KpiTone = 'accent' | 'success' | 'info' | 'warning' | 'danger';

export interface KpiExtra {
  label: string;
  value: string;
  /** Değeri renklendirir (ör. müşteri bakiyesi artı yeşil / eksi kırmızı). Boşsa nötr. */
  tone?: 'success' | 'danger';
}

/**
 * KPI kartı (Faz 4).
 *
 * Yalnızca gösterim yapar; veriyi çağıran bileşen sağlar. Değer önceden
 * biçimlendirilmiş metin olarak gelir — kuyumculukta tek bir "toplam" olmadığı
 * için (TL / USD / Has ayrı) çoklu değer `extras` ile verilir.
 *
 * Temaya göre iki çizim: modern temada `.m-kpi` (stilleri modern tema scope'unda,
 * `_dashboard.scss`), klasik temada Skote'nin `mini-stats-wid` kartı — `.m-kpi`
 * stilleri klasik temada olmadığı için kart orada stilsiz kalıyordu.
 */
@Component({
  selector: 'app-kpi-card',
  templateUrl: './kpi-card.component.html'
})
export class KpiCardComponent {
  constructor(private uiTheme: UiThemeService) {}

  get isModern(): boolean {
    return this.uiTheme.isModern;
  }

  /** Klasik temada simge zemini: Skote/Bootstrap renk adı ("accent" → primary). */
  get classicTone(): string {
    return this.tone === 'accent' ? 'primary' : this.tone;
  }

  @Input() label: string;
  @Input() value: string;
  @Input() icon = 'bx bx-bar-chart-alt-2';
  @Input() tone: KpiTone = 'accent';

  /** Yüzde değişim. null/undefined ise değişim satırı gösterilmez. */
  @Input() delta: number | null = null;

  /** Karşılaştırma etiketi, ör. "Dün: 12.400,00 TL". */
  @Input() comparison: string;

  /** Ek para birimi satırları. */
  @Input() extras: KpiExtra[] = [];

  @Input() loading = false;

  /**
   * İsteğe bağlı eylem düğmesi (ör. bakiye kartındaki "Hareket Ekle"). Simge verilirse
   * başlık satırının sağında küçük yuvarlak düğme çıkar; tıklanınca `action` yayınlanır.
   */
  @Input() actionIcon: string;
  @Input() actionTitle: string;
  @Output() action = new EventEmitter<void>();

  onAction(event: Event): void {
    // Kart tıklanabilir bir sarmalayıcı içindeyse onun tıklaması da tetiklenmesin.
    event.stopPropagation();
    this.action.emit();
  }

  get deltaLabel(): string {
    if (this.delta === null || this.delta === undefined) {
      return '';
    }
    const sign = this.delta > 0 ? '+' : '';
    return sign + this.delta.toFixed(2).replace('.', ',') + '%';
  }

  get isPositive(): boolean {
    return (this.delta || 0) >= 0;
  }
}
